// src/oura/merchant/DocumentLibrary.tsx
//
// Documents (redesign, 22 Oct 2026 feedback): everything we have sent the
// merchant, in one list, however it was made. The reports staff publish (the
// monthly and baseline reports, the quotation, the execution timeline, the
// PDFs of approved docs) and every doc shared with them (answer plans, Google
// profile packs, page wording, monthly updates). To do keeps only what needs
// them; this is the library.
//
// The list grows every month, so it can be searched, narrowed by type and by
// state, and sorted. Newest first by default, grouped by month; A to Z drops
// the groups. Everything is the server's: the dates, the states, the files.
// Nothing here works out a state.
//
// Both sources come from the one client-page read (`payload.reports` and
// `payload.documents`), so this needs no request of its own.

import { useMemo, useState } from "react";
import { FileTextIcon, SearchIcon, SearchXIcon } from "lucide-react";
import type { ClientPagePayload, ClientReportLink } from "@service/aeo/clientReview";
import type { MerchantDocument } from "@service/aeo/clientDocs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { CardEmpty, FoldCard, MERCHANT_DOC_STATUS, PillButton, Seg, TYPE, type SegItem } from "@oura/ui";
import { cn } from "../lib/cn";
import { clientArea } from "../merchantV2";
import { docCategory, docTitle, isDocFresh } from "./docAsks";
import { DocListRow } from "./Documents";
import { ReportLine } from "./ReportLine";
import { isPlanDocument, parseDay } from "./model";

/** The three kinds of thing in the library, in the merchant's words. */
type LibraryType = "report" | "plan" | "doc";

const TYPE_LABEL: Record<LibraryType, string> = {
    report: "Reports",
    plan: "Plans and quotes",
    doc: "Shared docs",
};

/** What a row can be narrowed to. "new" is either source; the rest are a shared doc's state. */
type StateFilter = "all" | "new" | "needs_your_reply" | "with_us" | "approved";
type SortKey = "newest" | "oldest" | "az";

const SORT_LABEL: Record<SortKey, string> = { newest: "Newest first", oldest: "Oldest first", az: "Name, A to Z" };

/** Rows drawn before "Show more": a year of monthly sends stays on one screen. */
const PAGE = 25;

type LibraryEntry =
    | { source: "report"; key: string; type: LibraryType; title: string; at: number | null; fresh: boolean; text: string; report: ClientReportLink }
    | { source: "doc"; key: string; type: LibraryType; title: string; at: number | null; fresh: boolean; text: string; doc: MerchantDocument };

/**
 * A report's type. The server's `kind` when it sends one; on live it is
 * absent today, so the engagement's own documents are told by their names.
 */
function reportType(r: Pick<ClientReportLink, "kind" | "title">): LibraryType {
    if (r.kind === "doc_approved") return "doc";
    return isPlanDocument(r) ? "plan" : "report";
}

/** ISO or a day string to a time; null when there is none to read. */
function timeOf(v: string | null | undefined): number | null {
    if (!v) return null;
    const t = Date.parse(v);
    if (Number.isFinite(t)) return t;
    return parseDay(v)?.getTime() ?? null;
}

/** Every report and shared doc as one list, unsorted. */
function libraryEntries(
    reports: readonly ClientReportLink[],
    docs: readonly MerchantDocument[],
    reportIsNew: (id: string) => boolean,
    readNow: ReadonlySet<string>,
): LibraryEntry[] {
    const fromReports: LibraryEntry[] = reports.map((r) => ({
        source: "report",
        key: `report:${r.id}`,
        type: reportType(r),
        title: r.title,
        at: timeOf(r.date),
        fresh: reportIsNew(r.id),
        text: [r.title, r.summary, ...(r.sections ?? [])].filter(Boolean).join(" ").toLowerCase(),
        report: r,
    }));
    const fromDocs: LibraryEntry[] = docs.map((d) => {
        // Sorted by its latest activity: a doc revised this week is news this week.
        const shared = timeOf(d.sharedAt);
        const updated = timeOf(d.updatedAt);
        const at = shared === null ? updated : updated === null ? shared : Math.max(shared, updated);
        // Named by its file, not its line 1 (#4731): a doc converted from the team's own Word file
        // opens on an internal header ("DELIVERABLE #S2 …"). Line 1 still finds it in the search.
        const lineOne = docTitle(d);
        const title = d.fileTitle?.trim() || lineOne;
        return {
            source: "doc",
            key: `doc:${d.id}`,
            type: "doc",
            title,
            at,
            fresh: isDocFresh(d, readNow),
            text: [title, lineOne, d.title, docCategory(d)].filter(Boolean).join(" ").toLowerCase(),
            doc: d,
        };
    });
    return [...fromReports, ...fromDocs];
}

function matchesState(e: LibraryEntry, state: StateFilter): boolean {
    if (state === "all") return true;
    if (state === "new") return e.fresh;
    return e.source === "doc" && e.doc.state === state;
}

function sortEntries(list: LibraryEntry[], sort: SortKey): LibraryEntry[] {
    const out = [...list];
    if (sort === "az") return out.sort((a, b) => a.title.localeCompare(b.title, "en-GB", { sensitivity: "base" }));
    // Undated rows go last whichever way the dates run.
    return out.sort((a, b) => {
        if (a.at === null || b.at === null) return a.at === b.at ? 0 : a.at === null ? 1 : -1;
        return sort === "newest" ? b.at - a.at : a.at - b.at;
    });
}

/** "October 2026", the group a dated row sits in. */
function monthOf(at: number | null): string {
    return at === null ? "No date" : new Date(at).toLocaleString("en-GB", { month: "long", year: "numeric" });
}

export function DocumentLibrary({
    payload,
    reports,
    documents,
    reportIsNew,
    readNow,
    onOpenReport,
    onViewDoc,
}: {
    payload: ClientPagePayload;
    reports: readonly ClientReportLink[];
    /** Absent: docs are not read for this business yet, so the list is reports only. */
    documents: readonly MerchantDocument[] | undefined;
    reportIsNew: (id: string) => boolean;
    readNow: ReadonlySet<string>;
    onOpenReport: (id: string) => void;
    onViewDoc: (id: string) => void;
}) {
    const [query, setQuery] = useState("");
    const [type, setType] = useState<LibraryType | "all">("all");
    const [state, setState] = useState<StateFilter>("all");
    const [sort, setSort] = useState<SortKey>("newest");
    const [shown, setShown] = useState(PAGE);

    const all = useMemo(() => libraryEntries(reports, documents ?? [], reportIsNew, readNow), [reports, documents, reportIsNew, readNow]);
    const q = query.trim().toLowerCase();

    // Each control's counts are of what the OTHER controls leave, so a count
    // is what pressing it shows. Hidden when 0, except "All".
    const searched = useMemo(() => (q ? all.filter((e) => e.text.includes(q)) : all), [all, q]);
    const typeCounts = useMemo(() => {
        const out: Record<LibraryType, number> = { report: 0, plan: 0, doc: 0 };
        for (const e of searched) if (matchesState(e, state)) out[e.type] += 1;
        return out;
    }, [searched, state]);
    const ofType = useMemo(() => (type === "all" ? searched : searched.filter((e) => e.type === type)), [searched, type]);
    const stateCount = (s: StateFilter) => ofType.filter((e) => matchesState(e, s)).length;
    const visible = useMemo(() => sortEntries(ofType.filter((e) => matchesState(e, state)), sort), [ofType, state, sort]);

    const filtered = q !== "" || type !== "all" || state !== "all";
    const clear = () => {
        setQuery("");
        setType("all");
        setState("all");
        setShown(PAGE);
    };
    // A narrower list starts again from the top.
    const narrow = <T,>(set: (v: T) => void) => (v: T) => {
        set(v);
        setShown(PAGE);
    };

    const typeItems: SegItem<LibraryType | "all">[] = [
        { key: "all", label: "All", count: (Object.values(typeCounts) as number[]).reduce((a, b) => a + b, 0) },
        ...(Object.keys(TYPE_LABEL) as LibraryType[])
            .filter((t) => typeCounts[t] > 0 || type === t)
            .map((t) => ({ key: t, label: TYPE_LABEL[t], count: typeCounts[t] })),
    ];
    // Only the states this list can hold: no doc states while docs are not read.
    const stateOptions: { key: StateFilter; label: string }[] = [
        { key: "new", label: "Not opened yet" },
        ...(documents
            ? (["needs_your_reply", "with_us", "approved"] as const).map((s) => ({ key: s as StateFilter, label: MERCHANT_DOC_STATUS[s].label }))
            : []),
    ];

    const rows = visible.slice(0, shown);
    // Grouped by month while sorted by date; a name sort reads as one list.
    const groups: { label: string; list: LibraryEntry[] }[] = [];
    for (const e of rows) {
        const label = sort === "az" ? "" : monthOf(e.at);
        const last = groups[groups.length - 1];
        if (last && last.label === label) last.list.push(e);
        else groups.push({ label, list: [e] });
    }

    return (
        <FoldCard
            id="reports"
            title="All documents"
            icon={{ icon: FileTextIcon, area: clientArea("setup") }}
            right={all.length > 0 ? (filtered ? `${visible.length} of ${all.length}` : String(all.length)) : undefined}
        >
            {all.length === 0 ? (
                <CardEmpty icon={FileTextIcon} title="Nothing here yet">
                    Every report and document we send you will be kept here. Your first comes with your first month&rsquo;s work.
                </CardEmpty>
            ) : (
                <>
                    {/* Search on its own line, so it keeps its width in a narrow card;
                        type, status and sort under it. */}
                    <div className="mb-3 space-y-2">
                        <label className="relative block w-full">
                            <span className="sr-only">Search documents</span>
                            <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => narrow(setQuery)(e.target.value)}
                                placeholder="Search by name or what's inside"
                                className="h-9 w-full rounded-xl border-0 bg-gray-50 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600"
                            />
                        </label>
                        <div className="flex flex-wrap items-center gap-2">
                            <Seg ariaLabel="Type" size="sm" items={typeItems} value={type} onChange={narrow(setType)} />
                            {filtered && (
                                <button type="button" onClick={clear} className={cn(TYPE.tc, "font-medium text-gray-500 hover:text-gray-700 hover:underline")}>
                                    Clear all
                                </button>
                            )}
                            <div className="ml-auto flex flex-wrap items-center gap-2">
                                <Select value={state} onValueChange={(v) => narrow(setState)(v as StateFilter)}>
                                    <SelectTrigger aria-label="Show" className="h-8 w-full rounded-xl text-sm sm:w-[180px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">
                                            Any status <span className="text-gray-400">({ofType.length})</span>
                                        </SelectItem>
                                        {stateOptions.map((o) => {
                                            const n = stateCount(o.key);
                                            if (n === 0 && state !== o.key) return null;
                                            return (
                                                <SelectItem key={o.key} value={o.key}>
                                                    {o.label} <span className="text-gray-400">({n})</span>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                                <Select value={sort} onValueChange={(v) => narrow(setSort)(v as SortKey)}>
                                    <SelectTrigger aria-label="Sort" className="h-8 w-full rounded-xl text-sm sm:w-[150px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                                            <SelectItem key={k} value={k}>
                                                {SORT_LABEL[k]}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    {visible.length === 0 ? (
                        <CardEmpty
                            icon={SearchXIcon}
                            title="No documents match"
                            action={<PillButton onClick={clear}>Clear search and filters</PillButton>}
                        >
                            {q ? `Nothing found for "${query.trim()}".` : "Nothing fits these filters."}
                        </CardEmpty>
                    ) : (
                        <div className="space-y-3">
                            {groups.map((g) => (
                                <section key={g.label || "all"} aria-label={g.label || undefined}>
                                    {g.label && (
                                        <p className={cn(TYPE.lab, "m-0 flex items-center gap-2 pb-1 pt-2")}>
                                            {g.label}
                                            <span className={cn(TYPE.tc, "font-normal tabular-nums text-gray-400")}>{g.list.length}</span>
                                        </p>
                                    )}
                                    {g.list.map((e) =>
                                        e.source === "report" ? (
                                            <ReportLine key={e.key} report={e.report} isNew={e.fresh} onOpen={() => onOpenReport(e.report.id)} />
                                        ) : (
                                            <DocListRow key={e.key} doc={e.doc} title={e.title} payload={payload} fresh={e.fresh} onView={() => onViewDoc(e.doc.id)} />
                                        ),
                                    )}
                                </section>
                            ))}
                            {visible.length > shown && (
                                <div className="pt-1 text-center">
                                    <PillButton onClick={() => setShown((n) => n + PAGE)}>
                                        Show {Math.min(PAGE, visible.length - shown)} more
                                    </PillButton>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}
        </FoldCard>
    );
}
